import React, { useEffect, useState } from 'react'
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Typography, Paper, Chip,
} from '@material-ui/core'
import { thousandsSeparate, toFarsiDigits, gregorianToJalali } from '../../assets/utils'
import api from '../../api/client'

/**
 * گزارشِ «بیمه‌های پرداختیِ قرارداد» — فهرستِ کاملِ سطرهای بیمه از اسناد و
 * انتقال‌ها به‌همراه تمامِ جزئیات و جمعِ هر ستون. خروجی Excel هم دارد.
 */
const num = (v) => (v === null || v === undefined || v === '') ? '—' : toFarsiDigits(thousandsSeparate(Number(v) || 0))
const jdate = (iso) => {
  if (!iso) return '—'
  try {
    // تاریخ‌ها از API به‌صورت میلادیِ ISO (YYYY-MM-DD) می‌آیند و gregorianToJalali
    // خودش رقم‌ها را فارسی می‌کند. (پیش‌تر با سه آرگومان عددی صدا زده می‌شد که
    // input.substring خطا می‌داد و همهٔ تاریخ‌ها «—» می‌شدند.)
    return gregorianToJalali(String(iso).slice(0, 10)) || '—'
  } catch (e) { return '—' }
}
const txt = (v) => (v === null || v === undefined || v === '') ? '—' : toFarsiDigits(String(v))

const DATE_KEYS = ['date', 'insurance_reference_date', 'cheque_date', 'expiry_date', 'invoice_date']
const MONEY_KEYS = ['amount', 'one_time_deduction', 'insurance', 'insurance_interest', 'insurance_deduction',
  'insurance_deduction_interest', 'transfer_insurance', 'transfer_insurance_interest']
const fmtFor = (key) => {
  if (DATE_KEYS.indexOf(key) >= 0) return (r) => jdate(r[key])
  if (MONEY_KEYS.indexOf(key) >= 0) return (r) => num(r[key])
  return (r) => txt(r[key])
}
const hasVal = (v) => v !== null && v !== undefined && v !== '' && v !== 0
// ستون‌ها را سرور تعیین می‌کند؛ اگر ستونی در هیچ سطری مقدار نداشته باشد نمایش داده نمی‌شود.
const colsFor = (data) => {
  const labels = (data && data.labels) || {}
  const rows = (data && data.rows) || []
  const ordered = Object.keys(labels).length
    ? Object.keys(labels)
    : ((data && data.columns) || [])
  const keys = ordered.filter((k) => k === 'source_label' || rows.some((r) => hasVal(r[k])))
  const safeKeys = keys.length ? keys : ['source_label', 'number', 'date', 'amount']
  return safeKeys.map((k) => [labels[k] || k, fmtFor(k), k])
}

const InsuranceReportDialog = ({ open, onClose, contractId }) => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open || !contractId) return
    let alive = true
    setLoading(true); setError(''); setData(null)
    api.get(`contracts/${contractId}/insurance-report/`)
      .then((res) => { if (alive) setData(res.data) })
      .catch((e) => { if (alive) setError((e && e.message) || 'دریافت گزارش انجام نشد.') })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [open, contractId])

  const openPrint = async () => {
    // پنجره را هم‌زمان با کلیک باز می‌کنیم تا مرورگر آن را به‌عنوان popup مسدود نکند.
    const win = window.open('', '_blank')
    try {
      // از همان کلاینتِ مشترک استفاده می‌کنیم تا آدرسِ درست (لوکال/سرور)، توکن (access)
      // و هدرها یکسان باشند؛ نسخهٔ قبلی آدرس را دستی می‌ساخت و توکن را با کلید اشتباه
      // می‌خواند، پس روی سرورِ لوکال درخواست به پروداکشن می‌رفت و شکست می‌خورد.
      const res = await api.get(`contracts/${contractId}/insurance-report/pdf/`, {
        params: { _ts: Date.now() },
        responseType: 'blob',
        headers: { Accept: 'application/pdf', 'Cache-Control': 'no-store', Pragma: 'no-cache' },
      })
      const blob = new Blob([res.data], { type: 'application/pdf' })
      const objectUrl = window.URL.createObjectURL(blob)
      if (win) {
        // PDF را داخل همان پنجره با iframe نشان می‌دهیم (به‌جای ناوبریِ مستقیم به blob
        // که در بعضی مرورگرها/حالت‌ها انجام نمی‌شود). کاربر از نمایشگرِ PDF چاپ/ذخیره می‌کند.
        win.document.open()
        win.document.write(
          '<!doctype html><html dir="rtl" lang="fa"><head><meta charset="utf-8">' +
          '<title>گزارش بیمه‌های پرداختیِ قرارداد</title></head>' +
          '<body style="margin:0">' +
          '<iframe src="' + objectUrl + '" title="گزارش بیمه‌های پرداختیِ قرارداد" ' +
          'style="position:fixed;inset:0;width:100%;height:100%;border:0"></iframe>' +
          '</body></html>'
        )
        win.document.close()
      } else {
        const a = document.createElement('a')
        a.href = objectUrl
        a.download = `insurance_report_${contractId}.pdf`
        document.body.appendChild(a); a.click(); document.body.removeChild(a)
      }
      setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60000)
    } catch (e) {
      if (win) win.close()
      const status = e && e.response && e.response.status
      alert(status === 401 || status === 403
        ? 'نشست شما پایان یافته است؛ دوباره وارد شوید.'
        : `خطا در خروجی چاپی${e && e.message ? ` (${e.message})` : ''}`)
    }
  }

  const downloadExcel = async () => {
    try {
      const res = await api.get(`contracts/${contractId}/insurance-report/excel/`, {
        params: { _ts: Date.now() },
        responseType: 'blob',
        headers: {
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'Cache-Control': 'no-store',
          Pragma: 'no-cache',
        },
      })
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `insurance_report_${contractId}.xlsx`
      document.body.appendChild(a); a.click(); document.body.removeChild(a)
      setTimeout(() => window.URL.revokeObjectURL(url), 60000)
    } catch (e) {
      const status = e && e.response && e.response.status
      alert(status === 401 || status === 403
        ? 'نشست شما پایان یافته است؛ دوباره وارد شوید.'
        : `خطا در خروجی Excel${e && e.message ? ` (${e.message})` : ''}`)
    }
  }

  const rows = (data && data.rows) || []
  const totals = (data && data.totals) || {}
  const contract = (data && data.contract) || {}
  const COLS = colsFor(data)

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle style={{ direction: 'rtl' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" style={{ gap: 10 }}>
            <Typography variant="h6" style={{ fontWeight: 800 }}>بیمه‌های پرداختیِ قرارداد</Typography>
            {contract.number ? <Chip size="small" label={`شماره: ${toFarsiDigits(contract.number)}`} /> : null}
            {contract.contractee ? <Chip size="small" label={contract.contractee} style={{ background: '#e8f5e9' }} /> : null}
            {data ? <Chip size="small" color="primary" label={`${toFarsiDigits(String(rows.length))} سطر`} /> : null}
          </Box>
          <Box style={{ display: 'flex', gap: 8 }}>
            <Button variant="contained" color="secondary" size="small" onClick={openPrint} disabled={!data || !rows.length}>
              چاپ
            </Button>
            <Button variant="contained" color="primary" size="small" onClick={downloadExcel} disabled={!data || !rows.length}>
              خروجی Excel
            </Button>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers style={{ direction: 'rtl' }}>
        {loading && <Box display="flex" justifyContent="center" style={{ padding: 30 }}><CircularProgress /></Box>}
        {error && <Typography color="error" style={{ padding: 12 }}>{error}</Typography>}
        {!loading && !error && data && (
          <TableContainer component={Paper} style={{ maxHeight: '62vh' }} elevation={0}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {COLS.map(([label]) => (
                    <TableCell key={label} align="center" style={{ whiteSpace: 'nowrap', fontWeight: 700, background: '#f0f6fb' }}>{label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={COLS.length} align="center" style={{ padding: 18 }}>برای این قرارداد بیمهٔ پرداختی ثبت نشده است.</TableCell></TableRow>
                )}
                {rows.map((r, i) => (
                  <TableRow key={i} hover>
                    {COLS.map(([label, get]) => (
                      <TableCell key={label} align="center" style={{ whiteSpace: 'nowrap' }}>{get(r)}</TableCell>
                    ))}
                  </TableRow>
                ))}
                {rows.length > 0 && (
                  <TableRow style={{ background: '#e9f7f5' }}>
                    {COLS.map(([label, , key], idx) => {
                      const v = MONEY_KEYS.indexOf(key) >= 0 ? totals[key] : undefined
                      return (
                        <TableCell key={key} align="center" style={{ fontWeight: 800, whiteSpace: 'nowrap' }}>
                          {idx === 0 ? 'جمع' : (v === undefined ? '' : num(v))}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" variant="contained">بستن</Button>
      </DialogActions>
    </Dialog>
  )
}

export default InsuranceReportDialog
